import React, { useState, useEffect, useCallback } from "react"
import { useParams, useLocation, useHistory } from "react-router-dom"

// Hooks
import { usePaginatedAPI, useModal, useForm } from "./hooks"

// Tabs
import { InfoTab, PlansTab, PaymentTab, EmailLogsTab, OtpLogsTab, NotiLogsTab, UserLogsTab, SubscriptionModal } from "./tabs"

// Components
import { LoginSuccessModal } from "./components"

// Styles
import {
	responsiveCSS,
	jsonHighlightCSS,
	tabContainerStyle,
	tabStyle,
	activeTabStyle,
	backButtonStyle,
	primaryButtonStyle,
} from "./styles/commonStyles"

// Constants
import { TYPOGRAPHY, SPACING } from "./constants/theme"

// APIs
import enewspaperApi from "../../libs/enewspaper"

const DEFAULT_TAB = "info"

const TAB_CONFIG = [
	{ key: "info", label: "ข้อมูลลูกค้า" },
	{ key: "plans", label: "แพ็คเกจ" },
	{ key: "payment", label: "ประวัติการชำระเงิน" },
	{ key: "email", label: "ประวัติการส่งอีเมล์" },
	{ key: "otp", label: "ประวัติการส่ง OTP" },
	{ key: "noti", label: "ประวัติการแจ้งเตือน" },
	{ key: "user", label: "บันทึกการดำเนินการ" },
]

const TAB_KEYS = TAB_CONFIG.map((tab) => tab.key)

const CustomerDetail = () => {
	const { id } = useParams()
	const location = useLocation()
	const history = useHistory()
	const currentHash = location?.hash ?? ""

	// Main states
	const [tab, setTab] = useState(() => {
		if (typeof window === "undefined") return DEFAULT_TAB
		const hashTab = window.location.hash.replace("#", "")
		return TAB_KEYS.includes(hashTab) ? hashTab : DEFAULT_TAB
	})
	const [deleted, setDeleted] = useState(false)
	const [refreshCount, setRefreshCount] = useState(0)
	const [customer, setCustomer] = useState(null)
	const [payments, setPayments] = useState([])
	const [paymentMethods, setPaymentMethods] = useState([])
	const [loading, setLoading] = useState(true)
	const [scheduleList, setScheduleList] = useState([])
	const [planList, setPlanList] = useState([])

	// Subscription modal
	const subscriptionModal = useModal()
	const subscriptionForm = useForm({ planId: "" })

	// Login success modal
	const loginSuccessModal = useModal()

	// Pagination hooks for different data types
	const emailLogs = usePaginatedAPI(enewspaperApi.getEmailLogs)
	const otpLogs = usePaginatedAPI(enewspaperApi.getOtpLogs)
	const notiLogs = usePaginatedAPI(enewspaperApi.getNotifications)
	const userLogs = usePaginatedAPI(enewspaperApi.getUserLogs)

	const resetState = useCallback(() => {
		setPayments([])
		setPaymentMethods([])
		setCustomer(null)
		emailLogs.resetPagination()
		otpLogs.resetPagination()
		notiLogs.resetPagination()
		userLogs.resetPagination()
	}, [emailLogs, otpLogs, notiLogs, userLogs])

	// Fetch main customer data
	useEffect(() => {
		const controller = new AbortController()

		async function fetchData() {
			setLoading(true)
			try {
				const [customerRes, paymentsRes, planListRes, scheduleRes] = await Promise.all([
					enewspaperApi.getCustomer(id, { signal: controller.signal }),
					enewspaperApi.getPayments(id, { signal: controller.signal }),
					enewspaperApi.getPlanList({ signal: controller.signal }),
					enewspaperApi.getScheduleList(id, { signal: controller.signal }),
				])

				setCustomer(customerRes)
				setPayments(paymentsRes)
				setPlanList(planListRes || [])
				setScheduleList(scheduleRes || [])

				// Fetch payment methods if we have a user ID
				if (customerRes?.userId) {
					try {
						const paymentMethodsRes = await enewspaperApi.getPaymentMethods(customerRes.userId, { signal: controller.signal })
						setPaymentMethods(paymentMethodsRes?.paymentMethods || [])
					} catch (paymentMethodsError) {
						console.error("Error loading payment methods:", paymentMethodsError)
						setPaymentMethods([])
					}
				}

				try {
					await Promise.all([
						emailLogs.fetchData(id, 1),
						otpLogs.fetchData(id, 1),
						notiLogs.fetchData(id, 1),
						customerRes.userId ? userLogs.fetchData(customerRes.userId, 1) : Promise.resolve(),
					])
				} catch (logError) {
					console.error("Error loading logs:", logError)
				}
			} catch (e) {
				if (e.name !== "AbortError") {
					resetState()
				}
			}
			setLoading(false)
		}

		if (id) fetchData()
		return () => controller.abort()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, refreshCount])

	const handleRefresh = useCallback(() => {
		setRefreshCount((prev) => prev + 1)
	}, [])

	const handleDeleteCustomer = useCallback(async () => {
		const confirmDelete = window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบลูกค้านี้?")
		if (confirmDelete) {
			const res = await enewspaperApi.deleteCustomer(customer.id)
			if (res === true) {
				resetState()
				setDeleted(true)
				alert("ลูกค้าถูกลบเรียบร้อยแล้ว")
			}
		}
	}, [customer, resetState])

	const handleDeleteMember = useCallback(async () => {
		const confirmDelete = window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบสมาชิกนี้?")
		if (confirmDelete) {
			const res = await enewspaperApi.deleteMember(customer.userId)
			if (res === true) {
				resetState()
				setDeleted(true)
				alert("สมาชิกถูกลบเรียบร้อยแล้ว")
			}
		}
	}, [customer, resetState])

	const handleDebugLogin = useCallback(async () => {
		if (!customer?.userId) {
			alert("ไม่พบ User ID ของลูกค้า")
			return
		}

		const confirmDebugLogin = window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการเข้าสู่ระบบ User ID: ${customer.userId}?`)
		if (confirmDebugLogin) {
			try {
				const result = await enewspaperApi.debugLogin({ id: customer.userId, remember: true })
				if (result) {
					loginSuccessModal.openModal()
				} else {
					alert("เข้าสู่ระบบไม่สำเร็จ")
				}
			} catch (error) {
				console.error("Login error:", error)
				alert("เกิดข้อผิดพลาดในการเข้าสู่ระบบ")
			}
		}
	}, [customer, loginSuccessModal])

	const handleSubscribeToPlan = useCallback(async () => {
		if (!subscriptionForm.values.planId) {
			alert("กรุณาเลือกแพ็คเกจที่ต้องการเพิ่ม")
			return
		}

		if (!customer?.id) {
			alert("ไม่พบข้อมูลลูกค้า")
			return
		}

		const selectedPlan = planList.find((plan) => plan.id === subscriptionForm.values.planId)
		const confirmSubscribe = window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการเพิ่มแพ็คเกจ "${selectedPlan?.name}" สำหรับลูกค้า ID: ${customer.id}?`)

		if (confirmSubscribe) {
			subscriptionForm.setLoading(true)
			try {
				const result = await enewspaperApi.subscribeCustomerToPlan(subscriptionForm.values.planId, { userId: customer.userId })
				if (result) {
					alert("เพิ่มแพ็คเกจสำเร็จ")
					handleRefresh()
					subscriptionModal.closeModal()
					subscriptionForm.reset()
				} else {
					alert("เพิ่มแพ็คเกจไม่สำเร็จ")
				}
			} catch (error) {
				console.error("Subscription error:", error)
				alert("เกิดข้อผิดพลาดในการเพิ่มแพ็คเกจ")
			}
			subscriptionForm.setLoading(false)
		}
	}, [subscriptionForm, customer, planList, handleRefresh, subscriptionModal])

	const handleUpdateExpiredPlan = useCallback(async () => {
		if (!customer?.id) {
			alert("ไม่พบข้อมูลลูกค้า")
			return
		}

		const confirmUpdate = window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการอัปเดตแพ็คเกจหมดอายุสำหรับลูกค้า ID: ${customer.id}?`)

		if (confirmUpdate) {
			try {
				const result = await enewspaperApi.updateExpiredPlan(customer.id)
				if (result) {
					alert("อัปเดตแพ็คเกจหมดอายุสำเร็จ")
					handleRefresh()
				} else {
					alert("อัปเดตแพ็คเกจหมดอายุไม่สำเร็จ")
				}
			} catch (error) {
				console.error("Update expired plan error:", error)
				alert("เกิดข้อผิดพลาดในการอัปเดตแพ็คเกจหมดอายุ")
			}
		}
	}, [customer, handleRefresh])

	useEffect(() => {
		const hashTab = currentHash.replace("#", "")
		if (hashTab && TAB_KEYS.includes(hashTab)) {
			if (hashTab !== tab) {
				setTab(hashTab)
			}
			return
		}
		if (hashTab && !TAB_KEYS.includes(hashTab)) {
			if (tab !== DEFAULT_TAB) {
				setTab(DEFAULT_TAB)
			}
			history.replace(`${location.pathname}${location.search || ""}#${DEFAULT_TAB}`)
			return
		}
		if (!hashTab && tab !== DEFAULT_TAB) {
			setTab(DEFAULT_TAB)
		}
	}, [currentHash, history, location.pathname, location.search, tab])

	const handleTabChange = useCallback(
		(nextTab) => {
			if (!TAB_KEYS.includes(nextTab)) return
			setTab(nextTab)
			history.replace(`${location.pathname}${location.search || ""}#${nextTab}`)
		},
		[history, location.pathname, location.search]
	)

	const getTabStyle = (key, isLast) => ({
		...(tab === key ? activeTabStyle : tabStyle),
		borderRight: isLast ? "none" : tabStyle.borderRight,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 4,
		position: "relative",
		overflow: "hidden",
		fontSize: TYPOGRAPHY.fontSize.md,
	})

	return (
		<>
			<style>{responsiveCSS}</style>
			<style>{jsonHighlightCSS}</style>

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					margin: "10px 20px",
				}}
			>
				<button onClick={() => window.history.back()} style={backButtonStyle}>
					← กลับหน้ารายการ
				</button>

				<button onClick={handleRefresh} style={primaryButtonStyle} title="Refresh data">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 4 }}>
						<path
							d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c2.9 0 5.5 1.4 7.1 3.5"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path d="M21 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
					Refresh
				</button>
			</div>

			<div
				style={{
					fontFamily: TYPOGRAPHY.fontFamily,
					width: "100%",
					minHeight: "100vh",
					display: "flex",
					justifyContent: "center",
					paddingLeft: SPACING.lg,
					paddingRight: SPACING.lg,
				}}
			>
				<div style={{ /*maxWidth: 1200,*/ width: "100%" }}>
					{/* Tab Navigation */}
					<div style={tabContainerStyle}>
						{TAB_CONFIG.map((tabObj, idx) => (
							<button key={tabObj.key} onClick={() => handleTabChange(tabObj.key)} style={getTabStyle(tabObj.key, idx === TAB_CONFIG.length - 1)}>
								{tabObj.label}
							</button>
						))}
					</div>

					{/* Tab Content */}
					<div style={{ marginTop: 0 }}>
						{tab === "info" && (
							<InfoTab
								customer={customer}
								paymentMethods={paymentMethods}
								scheduleList={scheduleList}
								loading={loading}
								deleted={deleted}
								onDeleteCustomer={handleDeleteCustomer}
								onDeleteMember={handleDeleteMember}
								onDebugLogin={handleDebugLogin}
								onSubscribe={subscriptionModal.openModal}
								onUpdateExpiredPlan={handleUpdateExpiredPlan}
							/>
						)}
						{tab === "plans" && <PlansTab customer={customer} loading={loading} />}
						{tab === "payment" && <PaymentTab payments={payments} loading={loading} />}
						{tab === "email" && <EmailLogsTab {...emailLogs} id={id} />}
						{tab === "otp" && <OtpLogsTab {...otpLogs} id={id} />}
						{tab === "noti" && <NotiLogsTab {...notiLogs} id={id} />}
						{tab === "user" && <UserLogsTab {...userLogs} userId={customer?.userId} />}
					</div>
				</div>
			</div>

			{/* Subscription Modal */}
			<SubscriptionModal
				isOpen={subscriptionModal.isOpen}
				onClose={subscriptionModal.closeModal}
				customer={customer}
				planList={planList}
				form={subscriptionForm}
				onSubscribe={handleSubscribeToPlan}
			/>

			{/* Login Success Modal */}
			<LoginSuccessModal isOpen={loginSuccessModal.isOpen} onClose={loginSuccessModal.closeModal} />
		</>
	)
}

export default CustomerDetail
